import axios from "axios";
import axiosRetry from 'axios-retry';

import { AUTH_TOKEN } from "../constants/Auth";

import { APP_PREFIX_PATH, AUTH_PREFIX_PATH, MANAGE_PREPIX_PATH } from 'configs/AppConfig'
import { Modal } from 'antd';
import { API_BASE_URL } from 'configs/AppConfig'
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from "uuid";
import { PATH_VARIABLE } from "constants/ApiConstant";
import trace from "auth/trace";
import {
  StartIdleTimer,
  clearIdleSession,
  startFreshIdleSession,
  markActivity,
  stopIdleTimer,
} from "./idleTimer";
import { tabRefs } from "utils/tabManager";

const DEFAULT_IDLE_TIME_SECONDS = 600; // 10 minutes

const axiosConfig = axios.create({

  headers: {

    "Content-Type": "application/json",

    accept: "application/json",

  },

  timeout: 600000,

  responseType: "json",

});

const closeOpenedAppTabs = () => {
  tabRefs.forEach((tab) => {
    if (tab && !tab.closed) {
      tab.close();
    }
  });
};

const resetIdleExpiryUiState = () => {
  isLogoutTriggered = false;
  isIdleModalVisible = false;
  idleSessionFinishPending = false;
  stopIdleTimer();
  idleTimerInitialized = false;
  Modal.destroyAll();
};

const signOutAndGoToLogin = () => {
  const username = localStorage.getItem("username");
  const headers = {
    Authorization: `${localStorage.getItem(AUTH_TOKEN)}`,
    refreshToken: `${localStorage.getItem("refresh_token")}`,
    username: `${username}`,
  };

  try {
    const traceObj = {
      type: "sign_out",
      event: "click",
      message: `Sign out for username=${username}`,
      status: "Sign out successfully ",
      action: "Clicked sign out",
      referer_action_id: localStorage.getItem("referer_action_id"),
      trackId: uuidv4(),
      user_session: sessionStorage.getItem("user_session"),
    };
    localStorage.setItem("referer_action_id", traceObj.trackId);
    trace({ traceObj });
  } catch (error) {
    console.error("Failed to trace session expiry sign out:", error);
  }

  axios
    .get(`${API_BASE_URL}${MANAGE_PREPIX_PATH}${AUTH_PREFIX_PATH}/logout`, {
      headers,
    })
    .catch(() => {});

  const idwAuth = localStorage.getItem("idw_auth");
  clearIdleSession();
  localStorage.clear();
  if (idwAuth) {
    localStorage.setItem("idw_auth", idwAuth);
  }
  sessionStorage.clear();

  resetIdleExpiryUiState();
  closeOpenedAppTabs();

  window.location.replace(`${PATH_VARIABLE}${AUTH_PREFIX_PATH}/login`);
};

const closeTabAfterSessionExpiry = () => {
  idleSessionFinishPending = false;
  clearIdleSession();
  resetIdleExpiryUiState();

  try {
    window.close();
  } catch (error) {
    // window.close is only allowed for script-opened windows
  }

  if (!window.closed) {
    signOutAndGoToLogin();
  }
};

let isLogoutTriggered = false;
let isIdleModalVisible = false;
let idleTimerInitialized = false;
let idleSessionFinishPending = false;

export const resetIdleTimeout = () => {
  stopIdleTimer();
  clearIdleSession();
  idleTimerInitialized = false;
  isLogoutTriggered = false;
  isIdleModalVisible = false;
  idleSessionFinishPending = false;
};

const parseIdleTimeSeconds = (value) => {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return DEFAULT_IDLE_TIME_SECONDS;
  }
  return seconds;
};

const extractIdleTimeValue = (data) => {
  if (data == null || data === '') {
    return null;
  }
  if (typeof data === 'number' || typeof data === 'string') {
    return data;
  }
  if (Array.isArray(data)) {
    const match = data.find((item) => item?.parameterName === 'idleTime') || data[0];
    return match?.parameterValue ?? match?.value ?? null;
  }
  if (typeof data === "object") {
    if (Array.isArray(data.content)) {
      return extractIdleTimeValue(data.content);
    }
    return data.parameterValue ?? data.value ?? data.idleTime ?? null;
  }
  return null;
};

const handleIdle = () => {
  if (isLogoutTriggered || isIdleModalVisible) {
    return;
  }
  if (!localStorage.getItem(AUTH_TOKEN)) {
    return;
  }
  if (window.location.pathname.includes('/login')) {
    return;
  }

  isIdleModalVisible = true;
  isLogoutTriggered = true;
  idleSessionFinishPending = true;
  localStorage.setItem("idle", "true");

  Modal.confirm({
    title: "Your session has expired. Please log in again to continue.",
    icon: <ExclamationCircleOutlined />,
    okText: "Login",
    cancelText: "Close",
    closable: true,
    maskClosable: false,
    keyboard: true,
    onOk() {
      idleSessionFinishPending = false;
      signOutAndGoToLogin();
    },
    onCancel() {
      closeTabAfterSessionExpiry();
    },
    afterClose() {
      isIdleModalVisible = false;
      if (idleSessionFinishPending && localStorage.getItem(AUTH_TOKEN)) {
        closeTabAfterSessionExpiry();
      }
    },
  });
};

const IDLE_SESSION_STARTED_KEY = 'idleSessionStarted';

const isNewAuthenticatedTabSession = () => {
  try {
    return !sessionStorage.getItem(IDLE_SESSION_STARTED_KEY);
  } catch (e) {
    return true;
  }
};

const startIdleTimerWithSeconds = (idleTimeSeconds, options = {}) => {
  const seconds = parseIdleTimeSeconds(idleTimeSeconds);
  localStorage.setItem('idleTime', String(seconds));
  StartIdleTimer(seconds, handleIdle, options);
};

/**
 * Start idle countdown after login / when a new tab session begins.
 * Clears any previous idle flag so the expire modal is not shown immediately.
 */
export const initIdleTimeout = async ({ freshStart = false } = {}) => {
  if (!localStorage.getItem(AUTH_TOKEN)) {
    return;
  }
  if (window.location.pathname.includes("/login")) {
    return;
  }

  if (freshStart) {
    stopIdleTimer();
    isLogoutTriggered = false;
    isIdleModalVisible = false;
    idleSessionFinishPending = false;
  } else if (idleTimerInitialized) {
    return;
  }

  // New tab / post-login: idle count must start from now, not from old lastActivity/idle flag
  const shouldFreshStart = freshStart || isNewAuthenticatedTabSession();
  if (shouldFreshStart) {
    startFreshIdleSession();
    isLogoutTriggered = false;
    isIdleModalVisible = false;
  }

  idleTimerInitialized = true;

  try {
    const response = await axiosConfig.get(
      `${API_BASE_URL}${MANAGE_PREPIX_PATH}${APP_PREFIX_PATH}/parameter/appParameter?parameterName=idleTime`,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN)}`,
          'accountUserId': localStorage.getItem('accountUserId'),
          'accountId': localStorage.getItem('accountId'),
        },
      }
    );
    const rawValue = extractIdleTimeValue(response?.data);
    const idleSeconds = rawValue == null || rawValue === ''
      ? DEFAULT_IDLE_TIME_SECONDS
      : parseIdleTimeSeconds(rawValue);
    if (shouldFreshStart) {
      markActivity();
    }
    startIdleTimerWithSeconds(idleSeconds, { freshStart: shouldFreshStart });
  } catch (error) {
    console.error('Failed to fetch idleTime system parameter:', error);
    const isMissingParameter = error?.response?.status === 404;
    const fallback = isMissingParameter
      ? DEFAULT_IDLE_TIME_SECONDS
      : (localStorage.getItem('idleTime') || DEFAULT_IDLE_TIME_SECONDS);
    if (shouldFreshStart) {
      markActivity();
    }
    startIdleTimerWithSeconds(fallback, { freshStart: shouldFreshStart });
  }
};

if (
  localStorage.getItem(AUTH_TOKEN) &&
  !window.location.pathname.includes("/login")
) {
  initIdleTimeout();
}

const retryCondition = error =>
!error.response || error.response.status === 504;

axiosRetry(axiosConfig, {
retries: 0,
retryCondition,
});

axiosConfig.interceptors.request.use((config) => {
  const token = localStorage.getItem(AUTH_TOKEN);
  if (
    token &&
    !idleTimerInitialized &&
    !window.location.pathname.includes("/login")
  ) {
    initIdleTimeout();
  }
  return config;
});

axiosConfig.interceptors.response.use( (response) => {
  // Remove the idle check here to prevent duplicate logout calls
  return response
}, async(error) => {

  const originalRequest = error.config;
  if (
    error?.response?.status === 401 &&
    !originalRequest?._retry &&
    (error?.response?.data?.error === 'Unauthorized' || error?.response?.data?.error === 'invalid_token' ) 
  ) {
    originalRequest._retry = true;
    if(localStorage.getItem("refresh_token")){
      try {
          console.log("refresh token in valid")
          const data = {'refresh_token': localStorage.getItem("refresh_token"),
                     'access_token': localStorage.getItem(AUTH_TOKEN)};
          return  await axios
            .post(API_BASE_URL+MANAGE_PREPIX_PATH+AUTH_PREFIX_PATH+"/refresh-token", data,  { 
              headers: {'access_token': localStorage.getItem("access_token")}
            })
            .then(({ data }) => {
              originalRequest.headers['Authorization'] =
                'Bearer ' + data.accessToken;
              localStorage.setItem(AUTH_TOKEN, data.accessToken);
              // Remove idle check here to prevent duplicate logout calls
              return axios(originalRequest);
            // }else{
            //   window.location.replace("/admin/auth/login");
            // }
           }).catch(({error})=>{
            console.log(error);
             window.location.replace("/admin/auth/login");
            // }
           });
      } catch (error) {
        console.error('Invalid token:', error);
    }
    }else{
      window.location.replace("/admin/auth/login");
    }
  }
  // logout();
  return Promise.reject(error);
});



export default axiosConfig;